export interface OrderTotalsRow {
  key: string
  label: string
  amount: number
  // Rendered as -$x — a credit against what's owed, not a negative amount.
  negative?: boolean
  tone?: 'cancelled' | 'pending'
  strong?: boolean
  dividerBefore?: boolean
}

export interface OrderTotalsOptions {
  hiddenFeesLabel?: string
  // Portal settings — the support portal leaves both on.
  showTax?: boolean
  showShipping?: boolean
}

const num = (value: unknown): number => {
  const parsed = parseFloat(String(value ?? '0'))
  return isNaN(parsed) ? 0 : parsed
}

const isPositive = (value: unknown): boolean => num(value) > Number.EPSILON

// Charges, then payments — the two groups render as separate blocks.
export function buildOrderTotals(order: any, opts: OrderTotalsOptions = {}): OrderTotalsRow[][] {
  const o = order ?? {}
  const showHiddenFees = isPositive(o.hidden_fees)

  const charges: OrderTotalsRow[] = [{ key: 'subtotal', label: 'Subtotal', amount: num(o.subtotal) }]

  // Shipping stays visible at zero unless a fees line has taken its place.
  if (opts.showShipping !== false && (isPositive(o.shipping_price) || !showHiddenFees)) {
    charges.push({ key: 'shipping', label: 'Shipping', amount: num(o.shipping_price) })
  }
  if (showHiddenFees) {
    charges.push({ key: 'hidden_fees', label: opts.hiddenFeesLabel || 'Fees', amount: num(o.hidden_fees) })
  }
  if (isPositive(o.discount)) {
    charges.push({ key: 'discount', label: 'Discount', amount: num(o.discount), negative: true, tone: 'cancelled' })
  }
  if (opts.showTax !== false && isPositive(o.tax)) {
    charges.push({ key: 'tax', label: 'Tax', amount: num(o.tax) })
  }
  if (isPositive(o.tip)) {
    charges.push({ key: 'tip', label: 'Tip', amount: num(o.tip) })
  }
  charges.push({
    key: 'total',
    label: 'Total',
    amount: num(o.total_discounted_price),
    strong: true,
    dividerBefore: true,
  })

  const payments: OrderTotalsRow[] = []
  if (isPositive(o.past_balance)) {
    payments.push({ key: 'past_balance', label: 'Past Payments', amount: num(o.past_balance) })
  }
  if (isPositive(o.forward_balance)) {
    payments.push({
      key: 'forward_balance',
      label: 'Carry Forward',
      amount: num(o.forward_balance),
      negative: true,
      tone: 'cancelled',
    })
  }
  payments.push({ key: 'amount_paid', label: 'Amount Paid', amount: num(o.amount_paid) })
  if (isPositive(o.uncaptured_auth)) {
    payments.push({ key: 'uncaptured_auth', label: 'Authorized', amount: num(o.uncaptured_auth) })
  }
  if (isPositive(o.store_debit)) {
    payments.push({ key: 'store_debit', label: 'Credited', amount: num(o.store_debit) })
  }
  if (isPositive(o.amount_voided)) {
    payments.push({
      key: 'amount_voided',
      label: 'Voided',
      amount: num(o.amount_voided),
      negative: true,
      tone: 'cancelled',
    })
  }
  if (isPositive(o.settled_refund_amount)) {
    payments.push({
      key: 'settled_refund_amount',
      label: 'Refunded',
      amount: num(o.settled_refund_amount),
      negative: true,
      tone: 'cancelled',
    })
  }
  if (isPositive(o.pending_refund_amount)) {
    payments.push({
      key: 'pending_refund_amount',
      label: 'Pending Refund',
      amount: num(o.pending_refund_amount),
      negative: true,
      tone: 'pending',
    })
  }
  if (isPositive(o.store_credit)) {
    payments.push({
      key: 'store_credit',
      label: 'Store Credit',
      amount: num(o.store_credit),
      negative: true,
      tone: 'cancelled',
    })
  }
  if (isPositive(o.chargeback_amount)) {
    payments.push({
      key: 'chargeback_amount',
      label: 'Chargeback',
      amount: num(o.chargeback_amount),
      negative: true,
      tone: 'cancelled',
    })
  }
  payments.push({
    key: 'total_paid',
    label: 'Total Paid',
    amount: num(o.total_remaining_balance),
    strong: true,
    dividerBefore: true,
  })
  if (isPositive(o.amount_due)) {
    payments.push({ key: 'amount_due', label: 'Amount Due', amount: num(o.amount_due), strong: true })
  }

  return [charges, payments]
}

export const PENDING_REFUND_HELP =
  'The refund is pending because the payment has not been captured yet.\n' +
  'We will be able to process the refund once the charge is captured.\n' +
  'This may take a few business days to appear.'
